import { useEffect, useState } from 'react';
import { Modal } from 'react-daisyui';
import { CalendarCheck, Check, MapPin, X } from 'lucide-react';
import { formatDate, stripHTML } from 'utils/helpers';
import Success from './Success';
import { getPractice } from 'library/auth';

const ConfirmAppointmentModal = ({ open, onClose, appointment, onConfirm, loading = false }) => {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  async function handleConfirm() {
    try {
      await onConfirm();
      setConfirmed(true);
    } catch {}
  }

  const visitDate = appointment?.date ? formatDate(appointment.date, 'short') : '';
  const address = getPractice()?.address ? stripHTML(getPractice().address) : '';
  const timeRange = [appointment?.startTime, appointment?.endTime].filter(Boolean).join(' – ');
  const visitMeta = [
    appointment?.provider?.name,
    appointment?.provider?.role,
    appointment?.durationMinutes ? `${appointment.durationMinutes} min` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ margin: 0 }}
      backdrop
      className="relative flex max-h-[calc(100dvh-1.5rem)] w-[92%] max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-100 p-0 shadow-2xl sm:max-h-[calc(100vh-4rem)]"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3.5 top-3.5 z-10 flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-secondary-500 transition-colors hover:bg-gray-200 hover:text-secondary-700"
      >
        <X size={16} />
      </button>

      {confirmed ? (
        <Success
          visitDate={visitDate}
          startTime={appointment?.startTime}
          onDone={onClose}
        />
      ) : (
        <div className="overflow-y-auto px-6 pb-6 pt-8 text-center sm:px-7">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-100">
            <CalendarCheck size={30} className="text-success-600" />
          </div>

          <h3 className="mt-3.5 text-lg font-bold text-secondary-900">Ready to confirm your visit?</h3>
          <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-secondary-500">
            Tap confirm and we&apos;ll send your request to the practice right away.
          </p>

          <div className="mt-5 flex items-center gap-3.5 rounded-xl border border-gray-200 p-3.5 text-left">
            {appointment?.date && (
              <div className="w-14 shrink-0 overflow-hidden rounded-lg border border-gray-200">
                <p className="bg-secondary-900 py-0.5 text-center text-[10px] font-bold uppercase tracking-wider text-white">
                  {formatDate(appointment.date, 'MMM')}
                </p>
                <div className="bg-white py-1.5 text-center">
                  <p className="text-xl font-bold leading-none text-secondary-900">
                    {formatDate(appointment.date, 'D')}
                  </p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary-400">
                    {formatDate(appointment.date, 'ddd')}
                  </p>
                </div>
              </div>
            )}

            <div className="min-w-0">
              {timeRange && <p className="text-base font-bold text-secondary-900">{timeRange}</p>}
              {visitMeta && <p className="mt-1 text-sm text-secondary-500">{visitMeta}</p>}
              {address && (
                <p className="mt-1 flex items-start gap-1.5 text-sm text-secondary-500">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-secondary-400" />
                  <span>{address}</span>
                </p>
              )}
            </div>
          </div>

          <p className="mt-3.5 text-sm text-secondary-400">
            You&apos;ll get a chance to update your health or insurance info next — it&apos;s optional.
          </p>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="btn btn-neutral mt-4 h-11 min-h-11 w-full rounded-xl text-sm text-white"
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              <>
                <Check size={16} strokeWidth={2.8} />
                Confirm appointment
              </>
            )}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ConfirmAppointmentModal;
